import type { DateRange, ReportPeriodPreset } from './types';
import {
  CLINIC_TIMEZONE,
  addZonedDays,
  addZonedMonths,
  addZonedYears,
  endOfZonedDay,
  endOfZonedMonth,
  endOfZonedWeek,
  endOfZonedYear,
  parseClinicDate,
  startOfZonedDay,
  startOfZonedMonth,
  startOfZonedWeek,
  startOfZonedYear,
  zonedYmd,
} from './timezone';

export { CLINIC_TIMEZONE };

export function resolveReportPeriod(
  period: ReportPeriodPreset,
  customFrom: string | null,
  customTo: string | null,
  now: Date = new Date(),
  timeZone = CLINIC_TIMEZONE,
): DateRange {
  switch (period) {
    case 'today':
      return { start: startOfZonedDay(now, timeZone), end: endOfZonedDay(now, timeZone) };
    case 'yesterday': {
      const day = addZonedDays(now, -1, timeZone);
      return { start: startOfZonedDay(day, timeZone), end: endOfZonedDay(day, timeZone) };
    }
    case 'this_week':
      return { start: startOfZonedWeek(now, timeZone), end: endOfZonedWeek(now, timeZone) };
    case 'last_week': {
      const previous = addZonedDays(startOfZonedWeek(now, timeZone), -1, timeZone);
      return { start: startOfZonedWeek(previous, timeZone), end: endOfZonedWeek(previous, timeZone) };
    }
    case 'this_month':
      return { start: startOfZonedMonth(now, timeZone), end: endOfZonedMonth(now, timeZone) };
    case 'last_month': {
      const previous = addZonedMonths(startOfZonedMonth(now, timeZone), -1, timeZone);
      return { start: startOfZonedMonth(previous, timeZone), end: endOfZonedMonth(previous, timeZone) };
    }
    case 'this_year':
      return { start: startOfZonedYear(now, timeZone), end: endOfZonedYear(now, timeZone) };
    case 'last_year': {
      const previous = addZonedYears(startOfZonedYear(now, timeZone), -1, timeZone);
      return { start: startOfZonedYear(previous, timeZone), end: endOfZonedYear(previous, timeZone) };
    }
    case 'custom': {
      const start = customFrom
        ? parseClinicDate(customFrom, timeZone)
        : startOfZonedDay(now, timeZone);
      const end = customTo
        ? endOfZonedDay(parseClinicDate(customTo, timeZone), timeZone)
        : endOfZonedDay(now, timeZone);
      return { start, end };
    }
  }
}

function formatInZone(date: Date, locale: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(locale, { timeZone: CLINIC_TIMEZONE, ...options }).format(date);
}

export function formatPeriodLabel(range: DateRange, locale = 'en-GB'): string {
  const sameDay = zonedYmd(range.start) === zonedYmd(range.end);
  const full: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
  if (sameDay) return formatInZone(range.start, locale, full);
  return `${formatInZone(range.start, locale, full)} – ${formatInZone(range.end, locale, full)}`;
}

/** Same-year ranges drop the repeated year so PDF headers stay on one line. */
export function formatCompactPeriodLabel(range: DateRange, locale = 'en-GB'): string {
  const sameDay = zonedYmd(range.start) === zonedYmd(range.end);
  if (sameDay) return formatPeriodLabel(range, locale);
  const startParts = zonedYmd(range.start).split('-');
  const endParts = zonedYmd(range.end).split('-');
  if (startParts[0] !== endParts[0]) return formatPeriodLabel(range, locale);
  const startLabel = formatInZone(range.start, locale, { day: 'numeric', month: 'long' });
  const endLabel = formatInZone(range.end, locale, { day: 'numeric', month: 'long', year: 'numeric' });
  return `${startLabel} – ${endLabel}`;
}

export function toIsoRange(range: DateRange): { start: string; end: string } {
  return { start: range.start.toISOString(), end: range.end.toISOString() };
}
